import { useQueryClient } from '@tanstack/react-query';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useLatest, useUpdateEffect } from 'react-use';
import { Card, styled } from '@mui/material';

import { DrugsApi } from '@/api/domains/drugs.api';
import { DrugsQueryKeys } from '@/api/domains/drugs.query-keys';
import type { Instruction } from '@/api/domains/drugs.types';
import { useGlobalErrorStore } from '@/components/dialogs';
import { FormProvider } from '@/components/form-controls';
import { initialNode, RHFTextEditor } from '@/components/form-controls/RHFTextEditor';
import { ModalBodyContent } from '@/components/Modal';
import { ModalBody } from '@/components/Modal/ModalBody2';
import { NomenclatureConfirmationEditDialog } from '@/components/nomenclature/dialogs/NomenclatureConfirmationEditDialog';
import { nomenclatureCreateEvent } from '@/components/nomenclature/events';
import {
  useNomenclatureContextWindows,
  useNomenclatureStoreActions,
  useNomenclatureStorePreferredTab,
} from '@/components/nomenclature/useNomenclatureStore';
import type { RecordStatusId } from '@/shared/constants/record-status.constants.ts';

import { NomenclatureModalSidebar } from '../NomenclatureModalSidebar';
import type { InstructionTabProps } from './InstructionTab';
import { useUpdateInstruction } from './useUpdateInstruction';

const InstructionContainer = styled(Card)({
  minHeight: '100%',
  padding: 'unset',
  height: '100%',
});

type FormSchema = {
  instruction: string;
  record_status_id?: RecordStatusId;
};

export type InstructionFormProps = InstructionTabProps & {
  instruction?: Instruction['instruction'];
};

const CURRENT_TAB_NAME = 'instruction';

export const InstructionForm = (props: InstructionFormProps) => {
  const { drug, variant, instruction, onClose } = props;

  const queryClient = useQueryClient();
  const preferredTab = useNomenclatureStorePreferredTab();
  const isEditMode = variant === 'edit';
  const isEditAfterCreationMode = variant === 'editAfterCreation';

  const methods = useForm<FormSchema>({
    defaultValues: {
      instruction: instruction || JSON.stringify(initialNode),
      record_status_id: drug?.record_status_id,
    },
  });

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit: wrapHandleSubmit,
  } = methods;

  const {
    closeConfirmationCreateDialog,
    closeConfirmationEditDialog,
    openConfirmationEditDialog,
    setActiveTab,
    setSelectedRow,
  } = useNomenclatureStoreActions();

  const { isConfirmationCreateDialogVisible, isConfirmationEditDialogVisible } = useNomenclatureContextWindows();
  const latestFormState = useLatest({ isDirty });

  useUpdateEffect(() => {
    if (preferredTab === CURRENT_TAB_NAME) {
      return;
    }

    const { isDirty } = latestFormState.current;

    if (isDirty) {
      openConfirmationEditDialog();
    } else {
      setActiveTab(preferredTab);
    }
  }, [preferredTab]);

  const { setGlobalError } = useGlobalErrorStore();
  const { mutateAsync: updateInstruction } = useUpdateInstruction();

  const submitHandler: SubmitHandler<FormSchema> = async formData => {
    if (!isDirty) {
      onClose();
      return;
    }

    try {
      const { instruction, record_status_id } = formData;
      await updateInstruction({ drug_id: drug.id, instruction });

      if (isConfirmationCreateDialogVisible) {
        closeConfirmationCreateDialog();
      }

      if (isConfirmationEditDialogVisible) {
        closeConfirmationEditDialog();
      }

      const updatedDrug = await DrugsApi.updateDrug(drug.id, { record_status_id });

      if (updatedDrug) {
        await queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());
        setSelectedRow(updatedDrug);
      }

      if (preferredTab !== CURRENT_TAB_NAME) {
        setActiveTab(preferredTab);
      }

      if (preferredTab === CURRENT_TAB_NAME) {
        nomenclatureCreateEvent.emit(drug);
        onClose();
      }
    } catch (error) {
      setGlobalError(error);
    }
  };

  const handleNomenclatureConfirmationEditDialogClose = () => {
    if (preferredTab === CURRENT_TAB_NAME) {
      onClose();
    } else {
      setActiveTab(preferredTab);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={submitHandler}>
      <ModalBody
        sidebar={
          <NomenclatureModalSidebar drug={drug} isEditMode={isEditMode || isEditAfterCreationMode} onCancel={onClose} />
        }
      >
        <ModalBodyContent>
          <InstructionContainer variant="outlined">
            <RHFTextEditor name="instruction" placeholder="Введите текст инструкции" />
          </InstructionContainer>
        </ModalBodyContent>
      </ModalBody>
      <NomenclatureConfirmationEditDialog
        isLoading={isSubmitting}
        onProceed={wrapHandleSubmit(submitHandler)}
        onClose={handleNomenclatureConfirmationEditDialogClose}
      />
    </FormProvider>
  );
};
